import { ScoreInputs } from '@/components/molecules/tie-breaker/ScoreInputs';

interface PlayingNowCardProps {
	nextToPlay: {
		teamOnePlayerOneName: string;
		teamOnePlayerTwoName?: string;
		teamTwoPlayerOneName: string;
		teamTwoPlayerTwoName?: string;
	} | null;
	teamOneScore: number | '';
	teamTwoScore: number | '';
	setTeamOneScore: (val: number | '') => void;
	setTeamTwoScore: (val: number | '') => void;
	createScoreHandleChange: (
		setter: (val: number | '') => void,
		tieBreakerRotatePlayerAfter: number
	) => (e: React.ChangeEvent<HTMLInputElement>) => void;
	tieBreakerRotatePlayerAfter: number;
	isMatchOver: boolean;
	isMatchOverAndCompleted: boolean;
	currentSequenceNumber: number;
	renderSaveButton: () => React.ReactNode;
	isPublicMatchResultPage?: boolean;
}

export const PlayingNowCard = ({
	nextToPlay,
	teamOneScore,
	teamTwoScore,
	setTeamOneScore,
	setTeamTwoScore,
	createScoreHandleChange,
	tieBreakerRotatePlayerAfter,
	isMatchOver,
	isMatchOverAndCompleted,
	currentSequenceNumber,
	renderSaveButton,
	isPublicMatchResultPage = false
}: PlayingNowCardProps) => {
	if (!nextToPlay) return null;

	return (
		<div className="text-center">
			{!isMatchOver && (
				<>
					{!isMatchOver && <p className="py-4 font-semibold text-sm text-left">Playing now:</p>}
					{/* MOBILE layout */}
					<div className="flex flex-col sm:hidden gap-4 border border-orange-300 rounded-lg border-darkorange-400 bg-darkorange-50 py-[18px] px-2 sm:px-4">
						<div className="flex items-center gap-2">
							<div className="flex-1 min-w-0">
								<PlayerBadge
									number={currentSequenceNumber}
									fullName={
										nextToPlay.teamTwoPlayerTwoName ? (
											<div className="flex flex-col leading-tight text-sm">
												<span>{nextToPlay.teamTwoPlayerOneName}</span>
												<span>{nextToPlay.teamTwoPlayerTwoName}</span>
											</div>
										) : (
											<span className="text-sm whitespace-nowrap">{nextToPlay.teamTwoPlayerOneName}</span>
										)
									}
								/>
							</div>

							<input
								type="number"
								min={0}
								max={tieBreakerRotatePlayerAfter > 0 ? tieBreakerRotatePlayerAfter : 99}
								className="custom-number-input w-10 h-10 border border-darkorange-300 rounded-md text-center text-lg shrink-0"
								value={teamTwoScore}
								onChange={createScoreHandleChange(setTeamTwoScore, tieBreakerRotatePlayerAfter)}
								disabled={isMatchOver || isPublicMatchResultPage}
							/>
						</div>

						<div className="flex items-center gap-2">
							<div className="flex-1 min-w-0">
								<PlayerBadge
									number={currentSequenceNumber}
									fullName={
										nextToPlay.teamOnePlayerTwoName ? (
											<div className="flex flex-col leading-tight text-sm">
												<span>{nextToPlay.teamOnePlayerOneName}</span>
												<span>{nextToPlay.teamOnePlayerTwoName}</span>
											</div>
										) : (
											<span className="text-sm whitespace-nowrap">{nextToPlay.teamOnePlayerOneName}</span>
										)
									}
								/>
							</div>

							<input
								type="number"
								min={0}
								max={tieBreakerRotatePlayerAfter > 0 ? tieBreakerRotatePlayerAfter : 99}
								className="custom-number-input w-10 h-10 border border-darkorange-300 rounded-md text-center text-lg shrink-0"
								value={teamOneScore}
								onChange={createScoreHandleChange(setTeamOneScore, tieBreakerRotatePlayerAfter)}
								disabled={isMatchOver || isPublicMatchResultPage}
							/>
						</div>
					</div>

					{/* DESKTOP layout */}
					<div className="hidden sm:grid sm:grid-cols-[200px_auto_200px] lg:grid-cols-[minmax(180px,1fr)_auto_minmax(180px,1fr)] sm:items-center sm:gap-4 justify-center border border-orange-300 rounded-lg border-darkorange-400 bg-darkorange-50 py-[18px] px-4 text-center">
						<PlayerBadge
							number={currentSequenceNumber}
							fullName={
								nextToPlay.teamTwoPlayerTwoName ? (
									<div className="flex flex-col leading-tight text-sm">
										<span>{nextToPlay.teamTwoPlayerOneName}</span>
										<span>{nextToPlay.teamTwoPlayerTwoName}</span>
									</div>
								) : (
									<span>{nextToPlay.teamTwoPlayerOneName}</span>
								)
							}
						/>

						<ScoreInputs
							teamOneScore={teamOneScore}
							teamTwoScore={teamTwoScore}
							onTeamOneChange={createScoreHandleChange(setTeamOneScore, tieBreakerRotatePlayerAfter)}
							onTeamTwoChange={createScoreHandleChange(setTeamTwoScore, tieBreakerRotatePlayerAfter)}
							disabled={isMatchOver || isPublicMatchResultPage}
							tieBreakerRotatePlayerAfter={tieBreakerRotatePlayerAfter}
						/>

						<PlayerBadge
							number={currentSequenceNumber}
							fullName={
								nextToPlay.teamOnePlayerTwoName ? (
									<div className="flex flex-col leading-tight text-sm">
										<span>{nextToPlay.teamOnePlayerOneName}</span>
										<span>{nextToPlay.teamOnePlayerTwoName}</span>
									</div>
								) : (
									<span>{nextToPlay.teamOnePlayerOneName}</span>
								)
							}
						/>
					</div>
				</>
			)}

			{!isMatchOverAndCompleted && !isPublicMatchResultPage && renderSaveButton()}
		</div>
	);
};

const PlayerBadge = ({ fullName, number }: { fullName?: React.ReactNode; number?: number }) => (
	<div className="text-orange-600 font-medium flex gap-2 items-center min-w-0 sm:justify-center">
		<span className="w-6 h-6 flex items-center justify-center aspect-square rounded-full border border-gray-400 bg-white text-sm shrink-0">
			{number}
		</span>
		<div className="line-clamp-2 text-darkorange-400 text-lg font-bold">{fullName}</div>
	</div>
);
